/** @format */

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Grid, Paper, TextField, Typography } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useApiFeedback } from "../../../../app/useApiFeedback";
import DisplayMoney from "../../../../common/custom/DisplayMoney";
import CircularLoader from "../../../../common/loaders/circularLoader";
import {
  fetchApplicantByIdThunk,
  selectApplicant,
} from "../../../applicant/ApplicantSlice";
import { AllApplicantDto, ApplicantViewDto } from "../../../applicant/Models";
import { selectAdmin } from "../../adminSlice";
import {
  AssessDto,
  GuardianAssessementAuditItemDto,
  MeansTestDto,
} from "../../models";
import {
  fetchAllApplicantThunk,
  GetDocumentsForApplicantThunk,
  setApplicationAssessmentThunk,
} from "../../thunks";
import AssessmentMeansTest from "./AssessmentMeansTest";
import { useParams } from "react-router";

interface Props {
  applicationId: number;
  application?: AllApplicantDto;
}
export default function AssessmentModal({ applicationId, application }: Props) {
  const [open, setOpen] = React.useState(false);
  const [numberOfGuardians, setNumberOfGuardians] = React.useState<number>(1);
  const { feedback, handleApiResponse } = useApiFeedback();
  const dispatch = useAppDispatch();
  const adminSelect = useAppSelector(selectAdmin);
  const [applicant, setApplicant] = useState<ApplicantViewDto>();
  const { year } = useParams();

  const selectApplicantData = useAppSelector(selectApplicant);

  const handleClickOpen = () => {
    if (applicationId) {
      dispatch(GetDocumentsForApplicantThunk(applicationId));
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const validationSchema = yup.object({
    reason: yup
      .string()
      .notRequired()
      .min(10, "If provided, comments must be at least 10 characters"),
    meansTests: yup.array().min(numberOfGuardians, "Please select required"),
  });

  const formik: FormikProps<AssessDto> = useFormik<AssessDto>({
    initialValues: {
      applicantId: 0,
      reason: "",
      meansTests: [] as MeansTestDto[],
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values: AssessDto) => {
      values.applicantId = applicationId;
      dispatch(setApplicationAssessmentThunk(values)).then((data: any) => {
        handleApiResponse(data, true);
      });
    },
  });

  useEffect(() => {
    if (feedback) {
      if (feedback.isSuccess) {
        handleClose();
        dispatch(fetchApplicantByIdThunk(formik.values.applicantId)).then(()=>{
          if (year) {
            dispatch(fetchAllApplicantThunk(parseInt(year))).then((data: any) => {
              handleApiResponse(data, false);
            });
          } else {
            dispatch(fetchAllApplicantThunk(new Date().getFullYear())).then(
              (data: any) => {
                handleApiResponse(data, false);
              }
            );
          }
        });
      }
    }
  }, [feedback]);

  const renderAssessmentGuardianResults = () => {
    return (
      <>
        <Box mb={3}>
          {applicant && !applicant.guardianAssessementAudit.isLegible ? (
            <Alert icon={<CloseIcon fontSize="inherit" />} severity="error">
              The applicant is ineligible for a Thuthuka bursary, reason: above
              threashhold
            </Alert>
          ) : (
            <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
              The applicant qualifies for thuthuka bursary
            </Alert>
          )}
        </Box>
        <Grid container spacing={2}>
          {applicant &&
            applicant.guardianAssessementAudit.guardian.map(
              (guardian: GuardianAssessementAuditItemDto, key: React.Key) => {
                return (
                  <Grid item xs={6}>
                    <Box className="meansTest_guardian_container">
                      <Box>
                        <Typography
                          variant="body2"
                          component="h2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {`${guardian.fullName}`}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" component="h2">
                          {guardian.identityNumber}
                        </Typography>
                        <Typography variant="body2" component="h2">
                          {guardian.employementStatus}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <DisplayMoney
                          amount={guardian.monthlyIncome}
                          label="Monthly Income"
                        />
                      </Box>
                      <Box>
                        <DisplayMoney
                          amount={guardian.annualIncome}
                          label="Yearly Income"
                        />
                      </Box>
                    </Box>
                  </Grid>
                );
              }
            )}
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Box>
            {applicant && (
              <DisplayMoney
                amount={
                  applicant.guardianAssessementAudit.combinedMonthlyIncome
                }
                label="Combined Monthly Income"
              />
            )}
          </Box>
          <Box>
            {applicant && (
              <DisplayMoney
                amount={applicant.guardianAssessementAudit.combineAnnualIncome}
                label="Combined Annual Income"
              />
            )}
          </Box>
        </Box>
      </>
    );
  };
  useEffect(() => {
    if (selectApplicantData.applicantState.applicant) {
      setApplicant(selectApplicantData.applicantState.applicant);
    }
  }, [selectApplicantData.applicantState.applicant]);

  return (
    <>
      <CircularLoader status={selectApplicantData.applicantState.status} />
      {selectApplicantData.applicantState.status === "idle" && (
        <>
          {" "}
          {application &&
          applicant &&
          applicant.guardianAssessementAudit.guardian.length === 0 &&
          applicant.canAssess ? (
            <>
              <AssessmentMeansTest
                formik={formik}
                application={application}
                fullApplicationDetails={applicant}
              />

              <Grid container spacing={2} mt={1}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="reason"
                    name="reason"
                    label="Enter Comments"
                    value={formik.values.reason}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    multiline
                    rows={3}
                    error={
                      formik.touched.reason && Boolean(formik.errors.reason)
                    }
                    helperText={formik.touched.reason && formik.errors.reason}
                  />
                </Grid>
                <Grid item xs={12}>
                  <LoadingButton
                    color="primary"
                    variant="contained"
                    size="medium"
                    startIcon={<SaveOutlinedIcon />}
                    onClick={() => {
                      formik.submitForm();
                    }}
                    loading={
                      adminSelect.applicationDatesState.status === "loading"
                    }
                    loadingPosition="start"
                  >
                    Save
                  </LoadingButton>
                </Grid>
              </Grid>
            </>
          ) : (
            <Box>
              {applicant &&
                applicant.guardianAssessementAudit &&
                applicant.guardianAssessementAudit.guardian.length > 0 && (
                  <Paper sx={{ padding: 1 }}>
                    {renderAssessmentGuardianResults()}
                  </Paper>
                )}
            </Box>
          )}
          {applicant &&
            !applicant.canAssess &&
            applicant.guardianAssessementAudit.guardian.length === 0 && (
              <Alert variant="standard" severity="warning">
                The provided data does not include quantification for the means
                test process.
              </Alert>
            )}
        </>
      )}
    </>
  );
}
